import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Upload, Download, Search, Trash2, Clock, AlertTriangle, X, FolderOpen, Shield, FileCheck, Printer, Sparkles, ScanLine, Lock, Plus, Eye, PenTool, LayoutTemplate, Edit, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import SignaturePad from './SignaturePad';

const DOC_TYPE_COLORS = {
  vehicle_title: '#F59E0B', bill_of_sale: '#10B981', purchase_agreement: '#3B82F6',
  buyers_order: '#8B5CF6', odometer_disclosure: '#EC4899', temporary_tag: '#F97316',
  registration: '#06B6D4', insurance: '#6366F1', inspection_report: '#14B8A6',
  emissions_certificate: '#84CC16', auction_receipt: '#EF4444', dealer_license: '#0EA5E9',
  tax_document: '#D946EF', warranty: '#22C55E', financing: '#A855F7',
  history_report: '#F43F5E', custom: '#64748B',
};

export default function DocumentCenter() {
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ doc_type: 'custom', vin: '', stock_number: '', customer_name: '', notes: '', expires_at: '' });
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const fileRef = useRef(null);

  // Document Generator state
  const [activeView, setActiveView] = useState('library'); // 'library' | 'generate' | 'scanner'
  const [templates, setTemplates] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('bill_of_sale');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [genData, setGenData] = useState({ sale_price: '', dealer_fee: '', sales_tax: '', total: '', payment_type: 'Cash', sale_date: '' });
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [generating, setGenerating] = useState(false);

  // AI Scanner state
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const scanRef = useRef(null);

  // Custom Template Builder state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [tplName, setTplName] = useState('');
  const [tplDesc, setTplDesc] = useState('');
  const [tplColor, setTplColor] = useState('#F59E0B');
  const [tplSections, setTplSections] = useState([{ title: 'Section 1', fields: [{ label: '', type: 'text', placeholder: '', key: '' }] }]);

  // Signature state
  const [savedSignatures, setSavedSignatures] = useState([]);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [activeSignatureField, setActiveSignatureField] = useState('');
  const [appliedSignatures, setAppliedSignatures] = useState({});

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') { setShowUpload(false); setShowAudit(false); setGeneratedHtml(''); } };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType !== 'all') params.doc_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      const { data } = await api.get('/api/documents', { params });
      setDocuments(data.documents);
      setTotal(data.total);
    } catch (e) { /* ignore */ }
  }, [search, filterType, filterStatus]);

  const fetchStats = useCallback(async () => {
    try { const { data } = await api.get('/api/documents/stats'); setStats(data); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
    api.get('/api/documents/types').then(r => setDocTypes(r.data)).catch(() => {});
    api.get('/api/documents/generate/templates').then(r => setTemplates(r.data)).catch(() => {});
    api.get('/api/documents/generate/vehicles').then(r => setVehicles(r.data)).catch(() => {});
    api.get('/api/documents/generate/customers').then(r => setCustomers(r.data)).catch(() => {});
    api.get('/api/documents/custom-templates').then(r => setCustomTemplates(r.data)).catch(() => {});
    api.get('/api/documents/signatures').then(r => setSavedSignatures(r.data)).catch(() => {});
  }, [fetchDocuments, fetchStats]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      Object.entries(uploadForm).forEach(([k, v]) => { if (v) formData.append(k, v); });
      await api.post('/api/documents/upload', formData);
      toast.success(`Uploaded ${file.name}`);
      setShowUpload(false);
      setUploadForm({ doc_type: 'custom', vin: '', stock_number: '', customer_name: '', notes: '', expires_at: '' });
      fetchDocuments();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDownload = async (id, filename) => {
    try {
      const response = await api.get(`/api/documents/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/api/documents/${id}`); toast.success('Deleted'); fetchDocuments(); fetchStats(); }
    catch { toast.error('Delete failed'); }
  };

  const handleBulkDownload = async () => {
    if (selectedDocs.length === 0) return;
    try {
      const response = await api.post('/api/documents/bulk-download', { doc_ids: selectedDocs }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `wakzy_docs_${Date.now()}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selectedDocs.length} documents`);
      setSelectedDocs([]);
    } catch { toast.error('Bulk download failed'); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedDocs.length} documents?`)) return;
    try { await api.post('/api/documents/bulk-delete', { doc_ids: selectedDocs }); toast.success('Deleted'); setSelectedDocs([]); fetchDocuments(); fetchStats(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleSelect = (id) => setSelectedDocs(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  const selectAll = () => { if (selectedDocs.length === documents.length) setSelectedDocs([]); else setSelectedDocs(documents.map(d => d.id)); };
  const formatSize = (bytes) => { if (bytes < 1024) return `${bytes} B`; if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`; return `${(bytes / (1024 * 1024)).toFixed(1)} MB`; };

  // Document Generation
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = {
        template_id: selectedTemplate,
        vehicle_id: (selectedVehicle && selectedVehicle !== 'none') ? selectedVehicle : undefined,
        customer_id: (selectedCustomer && selectedCustomer !== 'none') ? selectedCustomer : undefined,
        data: { ...genData, sale_date: genData.sale_date || new Date().toLocaleDateString('en-US') },
      };
      const { data } = await api.post('/api/documents/generate', payload);
      setGeneratedHtml(data.html);
      toast.success(`${data.name} generated!`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleGenerateBundle = async () => {
    setGenerating(true);
    try {
      const payload = {
        vehicle_id: (selectedVehicle && selectedVehicle !== 'none') ? selectedVehicle : undefined,
        customer_id: (selectedCustomer && selectedCustomer !== 'none') ? selectedCustomer : undefined,
        data: { ...genData, sale_date: genData.sale_date || new Date().toLocaleDateString('en-US') },
      };
      const { data } = await api.post('/api/documents/generate/bundle', payload);
      setGeneratedHtml(data.html);
      toast.success('Closing Package generated — 3 documents ready to print!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Bundle generation failed'); }
    finally { setGenerating(false); }
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(generatedHtml);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  // AI Scanner
  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/documents/scan', formData, { timeout: 60000 });
      if (data.success) {
        setScanResult(data.extracted);
        toast.success('Document scanned successfully!');
      } else {
        toast.error(data.error || 'Could not parse document');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); }
    finally { setScanning(false); if (scanRef.current) scanRef.current.value = ''; }
  };

  const applyScanToUpload = () => {
    if (!scanResult) return;
    setUploadForm(prev => ({
      ...prev,
      doc_type: scanResult.document_type || prev.doc_type,
      vin: scanResult.vin || prev.vin,
      stock_number: scanResult.stock_number || prev.stock_number,
      customer_name: scanResult.buyer_name || prev.customer_name,
      notes: scanResult.notes || prev.notes,
    }));
    setActiveView('library');
    setShowUpload(true);
    toast.success('Scan data applied to upload form');
  };

  // Custom Template Builder
  const handleSaveTemplate = async () => {
    if (!tplName.trim()) return toast.error('Template name required');
    if (tplSections.some(s => !s.title.trim())) return toast.error('All sections need titles');
    try {
      const payload = { name: tplName, description: tplDesc, header_color: tplColor, sections: tplSections };
      if (editingTemplate) {
        await api.put(`/api/documents/custom-templates/${editingTemplate}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/api/documents/custom-templates', payload);
        toast.success('Template created');
      }
      const { data } = await api.get('/api/documents/custom-templates');
      setCustomTemplates(data);
      resetTemplateForm();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const resetTemplateForm = () => {
    setShowTemplateBuilder(false);
    setEditingTemplate(null);
    setTplName('');
    setTplDesc('');
    setTplColor('#F59E0B');
    setTplSections([{ title: 'Section 1', fields: [{ label: '', type: 'text', placeholder: '', key: '' }] }]);
  };

  const editTemplate = (t) => {
    setEditingTemplate(t.id);
    setTplName(t.name);
    setTplDesc(t.description || '');
    setTplColor(t.header_color || '#F59E0B');
    setTplSections(t.sections || []);
    setShowTemplateBuilder(true);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/api/documents/custom-templates/${id}`);
      setCustomTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const generateFromCustom = async (templateId) => {
    setGenerating(true);
    try {
      const payload = {
        vehicle_id: (selectedVehicle && selectedVehicle !== 'none') ? selectedVehicle : undefined,
        customer_id: (selectedCustomer && selectedCustomer !== 'none') ? selectedCustomer : undefined,
        data: { ...genData, sale_date: genData.sale_date || new Date().toLocaleDateString('en-US') },
        signatures: appliedSignatures,
      };
      const { data } = await api.post(`/api/documents/custom-templates/${templateId}/generate`, payload);
      setGeneratedHtml(data.html);
      toast.success(`${data.name} generated!`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const addSection = () => setTplSections(prev => [...prev, { title: '', fields: [{ label: '', type: 'text', placeholder: '', key: '' }] }]);
  const removeSection = (i) => setTplSections(prev => prev.filter((_, idx) => idx !== i));
  const updateSection = (i, key, val) => setTplSections(prev => prev.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  const addField = (si) => setTplSections(prev => prev.map((s, i) => i === si ? { ...s, fields: [...s.fields, { label: '', type: 'text', placeholder: '', key: '' }] } : s));
  const removeField = (si, fi) => setTplSections(prev => prev.map((s, i) => i === si ? { ...s, fields: s.fields.filter((_, j) => j !== fi) } : s));
  const updateField = (si, fi, key, val) => setTplSections(prev => prev.map((s, i) => i === si ? { ...s, fields: s.fields.map((f, j) => j === fi ? { ...f, [key]: val } : f) } : s));

  // Signatures
  const handleSaveSignature = async (sigData) => {
    try {
      if (activeSignatureField) {
        setAppliedSignatures(prev => ({ ...prev, [activeSignatureField]: sigData }));
        setShowSignaturePad(false);
        setActiveSignatureField('');
        toast.success('Signature applied');
        return;
      }
      const { data } = await api.post('/api/documents/signatures', { signature_data: sigData, label: 'Dealer Signature' });
      setSavedSignatures(prev => [data, ...prev]);
      setShowSignaturePad(false);
      toast.success('Signature saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const deleteSignature = async (id) => {
    try {
      await api.delete(`/api/documents/signatures/${id}`);
      setSavedSignatures(prev => prev.filter(s => s.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="document-center" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
            <FolderOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Document Center</h2>
            <p className="text-sm text-gray-500">Generate, manage & scan dealer documents</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-xl text-sm" onClick={() => { setShowAudit(true); api.get('/api/documents/audit-log').then(r => setAuditLog(r.data)).catch(() => {}); }} data-testid="audit-log-btn">
            <Shield className="w-4 h-4 mr-1.5" />Audit Log
          </Button>
          <Button className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm" onClick={() => setShowUpload(true)} data-testid="upload-doc-btn">
            <Upload className="w-4 h-4 mr-1.5" />Upload
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit overflow-x-auto" data-testid="doc-view-tabs">
        {[
          { id: 'library', label: 'Library', icon: FolderOpen },
          { id: 'generate', label: 'Generate', icon: Printer },
          { id: 'templates', label: 'Templates', icon: LayoutTemplate },
          { id: 'signatures', label: 'Signatures', icon: PenTool },
          { id: 'scanner', label: 'AI Scanner', icon: ScanLine },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveView(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${activeView === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total', value: stats.total, icon: FileText, color: 'text-blue-600 bg-blue-50' },
            { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-600 bg-amber-50' },
            { label: 'Expiring', value: stats.expiring, icon: AlertTriangle, color: 'text-orange-600 bg-orange-50' },
            { label: 'Expired', value: stats.expired, icon: X, color: 'text-red-600 bg-red-50' },
            { label: 'Missing', value: stats.missing_alerts?.length || 0, icon: FileCheck, color: 'text-purple-600 bg-purple-50' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-7 h-7 rounded-lg ${s.color} flex items-center justify-center`}><s.icon className="w-3.5 h-3.5" /></div>
                <span className="text-xs text-gray-500">{s.label}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Missing Alerts */}
      {stats?.missing_alerts?.length > 0 && activeView === 'library' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="missing-alerts">
          <p className="text-sm font-semibold text-amber-800 mb-2"><AlertTriangle className="w-4 h-4 inline mr-1" />Missing Documents</p>
          <div className="flex flex-wrap gap-2">
            {stats.missing_alerts.slice(0, 5).map((a, i) => (
              <span key={i} className="text-xs bg-white border border-amber-200 rounded-lg px-2.5 py-1 text-amber-700">
                {a.vehicle} — {a.missing_type}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ==================== DOCUMENT LIBRARY VIEW ==================== */}
      {activeView === 'library' && (
        <>
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search VIN, stock #, customer, filename..." className="pl-10 rounded-xl" data-testid="doc-search" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-44 rounded-xl" data-testid="filter-type"><SelectValue placeholder="Doc Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {docTypes.map(dt => <SelectItem key={dt.id} value={dt.id}>{dt.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 rounded-xl" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedDocs.length > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="bulk-actions">
              <span className="text-sm font-medium text-blue-700">{selectedDocs.length} selected</span>
              <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={handleBulkDownload} data-testid="bulk-download-btn"><Download className="w-3.5 h-3.5 mr-1" />ZIP</Button>
              <Button size="sm" variant="outline" className="rounded-lg text-xs text-red-600 border-red-200" onClick={handleBulkDelete} data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
              <Button size="sm" variant="ghost" className="rounded-lg text-xs" onClick={() => setSelectedDocs([])} data-testid="clear-selection-btn">Clear</Button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3 w-8"><input type="checkbox" checked={selectedDocs.length === documents.length && documents.length > 0} onChange={selectAll} data-testid="select-all" /></th>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">VIN / Stock #</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {documents.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <FolderOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="font-medium">No documents found</p>
                    <p className="text-sm mt-1">Upload or generate your first document</p>
                  </td></tr>
                ) : documents.map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50 transition-colors" data-testid={`doc-row-${doc.id}`}>
                    <td className="px-4 py-3"><input type="checkbox" checked={selectedDocs.includes(doc.id)} onChange={() => toggleSelect(doc.id)} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: (DOC_TYPE_COLORS[doc.doc_type] || '#64748B') + '15', color: DOC_TYPE_COLORS[doc.doc_type] || '#64748B' }}>
                          <FileText className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{doc.filename}</p>
                          <p className="text-[10px] text-gray-400">{formatSize(doc.size)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-lg font-medium" style={{ backgroundColor: (DOC_TYPE_COLORS[doc.doc_type] || '#64748B') + '15', color: DOC_TYPE_COLORS[doc.doc_type] || '#64748B' }}>
                        {doc.doc_type_label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 font-mono">{doc.vin || doc.stock_number || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{doc.customer_name || '—'}</td>
                    <td className="px-4 py-3">
                      {doc.is_expired ? <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-600 font-medium">Expired</span>
                        : doc.is_expiring ? <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-600 font-medium">Expiring</span>
                        : <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-600 font-medium capitalize">{doc.status}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={() => handleDownload(doc.id, doc.filename)} data-testid={`download-${doc.id}`}><Download className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > 0 && <div className="px-4 py-3 border-t border-gray-50 text-xs text-gray-400">{total} document{total !== 1 ? 's' : ''}</div>}
          </div>
        </>
      )}

      {/* ==================== GENERATE DOCUMENTS VIEW ==================== */}
      {activeView === 'generate' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left — Form */}
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Printer className="w-5 h-5 text-amber-500" />Generate Document</h3>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Template</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {templates.map(t => (
                    <button key={t.id} onClick={() => t.available ? setSelectedTemplate(t.id) : toast.error(`Requires ${t.required_plan?.replace('_',' ')} plan`)}
                      className={`relative text-left p-3 rounded-xl border-2 transition-all ${selectedTemplate === t.id ? 'border-amber-500 bg-amber-50' : t.available ? 'border-gray-100 hover:border-gray-200' : 'border-gray-100 opacity-50'}`}
                      data-testid={`template-${t.id}`}>
                      {!t.available && <Lock className="w-3.5 h-3.5 absolute top-2 right-2 text-gray-400" />}
                      <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Vehicle (auto-fill)</label>
                <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="gen-vehicle"><SelectValue placeholder="Select vehicle..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vehicle</SelectItem>
                    {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer (auto-fill)</label>
                <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="gen-customer"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No customer</SelectItem>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sale Price</label>
                  <Input value={genData.sale_price} onChange={e => setGenData(p => ({ ...p, sale_price: e.target.value }))} placeholder="$0.00" className="rounded-xl mt-1" data-testid="gen-price" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dealer Fee</label>
                  <Input value={genData.dealer_fee} onChange={e => setGenData(p => ({ ...p, dealer_fee: e.target.value }))} placeholder="$0.00" className="rounded-xl mt-1" data-testid="gen-fee" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sales Tax</label>
                  <Input value={genData.sales_tax} onChange={e => setGenData(p => ({ ...p, sales_tax: e.target.value }))} placeholder="$0.00" className="rounded-xl mt-1" data-testid="gen-tax" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</label>
                  <Input value={genData.total} onChange={e => setGenData(p => ({ ...p, total: e.target.value }))} placeholder="$0.00" className="rounded-xl mt-1" data-testid="gen-total" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment Method</label>
                  <Input value={genData.payment_type} onChange={e => setGenData(p => ({ ...p, payment_type: e.target.value }))} placeholder="Cash" className="rounded-xl mt-1" data-testid="gen-payment" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date of Sale</label>
                  <Input type="date" value={genData.sale_date} onChange={e => setGenData(p => ({ ...p, sale_date: e.target.value }))} className="rounded-xl mt-1" data-testid="gen-date" />
                </div>
              </div>

              <Button onClick={handleGenerate} disabled={generating} className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white h-11" data-testid="generate-btn">
                {generating ? 'Generating...' : <><Printer className="w-4 h-4 mr-2" />Generate Document</>}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-gray-400 font-medium">OR</span></div>
              </div>

              <Button onClick={handleGenerateBundle} disabled={generating}
                className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white h-12 font-semibold shadow-lg" data-testid="bundle-btn">
                {generating ? 'Generating...' : <><Sparkles className="w-4 h-4 mr-2" />Generate Closing Package</>}
              </Button>
              <p className="text-[11px] text-gray-400 text-center -mt-2">Bill of Sale + Odometer Disclosure + Buyer's Order — all in one print</p>
            </div>
          </div>

          {/* Right — Preview */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden min-h-[500px]">
            {generatedHtml ? (
              <div>
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-700">Document Preview</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handlePrint} className="rounded-lg text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="print-btn">
                      <Printer className="w-3.5 h-3.5 mr-1" />Print / Save PDF
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setGeneratedHtml('')} className="rounded-lg text-xs"><X className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <iframe
                  srcDoc={generatedHtml}
                  className="w-full border-0"
                  style={{ height: '600px' }}
                  title="Document Preview"
                  data-testid="doc-preview-iframe"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-20 text-gray-400">
                <FileText className="w-12 h-12 mb-3 text-gray-300" />
                <p className="font-medium">Select a template and generate</p>
                <p className="text-sm mt-1">Documents auto-fill from vehicle & customer data</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== AI SCANNER VIEW ==================== */}
      {activeView === 'scanner' && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl border border-violet-200 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">AI Document Scanner</h3>
                  <p className="text-sm text-gray-500">Upload a document image — AI extracts VIN, customer info & amounts</p>
                </div>
              </div>

              <div className="border-2 border-dashed border-violet-300 rounded-xl p-8 text-center bg-white/50">
                <input ref={scanRef} type="file" accept="image/*" className="hidden" onChange={handleScan} data-testid="scan-file-input" />
                <ScanLine className="w-10 h-10 text-violet-400 mx-auto mb-3" />
                <Button onClick={() => scanRef.current?.click()} disabled={scanning}
                  className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white" data-testid="scan-btn">
                  {scanning ? 'Scanning...' : <><ScanLine className="w-4 h-4 mr-2" />Scan Document</>}
                </Button>
                <p className="text-xs text-gray-400 mt-3">Supports photos of titles, bills of sale, purchase agreements</p>
                <p className="text-[10px] text-violet-400 mt-1 font-medium">Enterprise Plan Feature</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h4 className="text-sm font-bold text-gray-700 mb-4">Extracted Data</h4>
            {scanResult ? (
              <div className="space-y-3">
                {[
                  { label: 'Document Type', key: 'document_type' },
                  { label: 'VIN', key: 'vin' },
                  { label: 'Year', key: 'year' },
                  { label: 'Make', key: 'make' },
                  { label: 'Model', key: 'model' },
                  { label: 'Color', key: 'color' },
                  { label: 'Mileage', key: 'mileage' },
                  { label: 'Buyer Name', key: 'buyer_name' },
                  { label: 'Buyer Address', key: 'buyer_address' },
                  { label: 'Sale Price', key: 'sale_price' },
                  { label: 'Total', key: 'total' },
                  { label: 'Sale Date', key: 'sale_date' },
                  { label: 'Notes', key: 'notes' },
                ].filter(f => scanResult[f.key]).map(f => (
                  <div key={f.key} className="flex items-center justify-between py-2 border-b border-gray-50" data-testid={`scan-${f.key}`}>
                    <span className="text-xs font-medium text-gray-500">{f.label}</span>
                    <span className="text-sm font-semibold text-gray-900">{String(scanResult[f.key])}</span>
                  </div>
                ))}
                <Button onClick={applyScanToUpload} className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 text-white mt-4" data-testid="apply-scan-btn">
                  <Plus className="w-4 h-4 mr-2" />Apply to Upload Form
                </Button>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Eye className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Scan results will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== CUSTOM TEMPLATES VIEW ==================== */}
      {activeView === 'templates' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-amber-500" />Custom Templates</h3>
            <Button onClick={() => { resetTemplateForm(); setShowTemplateBuilder(true); }} className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm" data-testid="new-template-btn">
              <Plus className="w-4 h-4 mr-1.5" />New Template
            </Button>
          </div>

          {/* Template Builder */}
          {showTemplateBuilder && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-gray-700">{editingTemplate ? 'Edit Template' : 'Create Template'}</h4>
                <button onClick={resetTemplateForm} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Name</label>
                  <Input value={tplName} onChange={e => setTplName(e.target.value)} placeholder="e.g. Warranty Agreement" className="rounded-xl mt-1" data-testid="tpl-name" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Accent Color</label>
                  <div className="flex gap-2 mt-1">
                    {['#F59E0B','#10B981','#3B82F6','#8B5CF6','#EF4444','#EC4899','#06B6D4'].map(c => (
                      <button key={c} onClick={() => setTplColor(c)} className={`w-8 h-8 rounded-lg border-2 ${tplColor === c ? 'border-gray-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Description (optional)</label>
                <Input value={tplDesc} onChange={e => setTplDesc(e.target.value)} placeholder="Brief description..." className="rounded-xl mt-1" data-testid="tpl-desc" />
              </div>

              {/* Sections */}
              {tplSections.map((section, si) => (
                <div key={si} className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-100" data-testid={`section-${si}`}>
                  <div className="flex items-center gap-2">
                    <Input value={section.title} onChange={e => updateSection(si, 'title', e.target.value)} placeholder="Section Title" className="rounded-lg text-sm font-semibold flex-1" data-testid={`section-title-${si}`} />
                    {tplSections.length > 1 && <button onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  {section.fields.map((field, fi) => (
                    <div key={fi} className="flex items-center gap-2" data-testid={`field-${si}-${fi}`}>
                      <Input value={field.label} onChange={e => updateField(si, fi, 'label', e.target.value)} placeholder="Field label" className="rounded-lg text-xs flex-1" />
                      <select value={field.type} onChange={e => updateField(si, fi, 'type', e.target.value)} className="h-9 px-2 rounded-lg border border-gray-200 text-xs bg-white">
                        <option value="text">Text</option>
                        <option value="currency">Currency $</option>
                        <option value="date">Date</option>
                        <option value="textarea">Long Text</option>
                        <option value="vin">VIN</option>
                        <option value="phone">Phone</option>
                        <option value="email">Email</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="signature">Signature</option>
                      </select>
                      <Input value={field.placeholder || ''} onChange={e => updateField(si, fi, 'placeholder', e.target.value)} placeholder="Auto-fill: {{vin}}" className="rounded-lg text-xs w-32" title="Use {{vin}}, {{buyer_name}}, etc." />
                      {section.fields.length > 1 && <button onClick={() => removeField(si, fi)} className="text-red-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => addField(si)} className="text-xs text-gray-500"><Plus className="w-3 h-3 mr-1" />Add Field</Button>
                </div>
              ))}
              <Button variant="outline" onClick={addSection} className="rounded-xl text-xs w-full" data-testid="add-section-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Section</Button>
              <Button onClick={handleSaveTemplate} className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white h-10" data-testid="save-template-btn">
                <Save className="w-4 h-4 mr-2" />{editingTemplate ? 'Update Template' : 'Save Template'}
              </Button>
            </div>
          )}

          {/* Template List */}
          {customTemplates.length === 0 && !showTemplateBuilder ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400">
              <LayoutTemplate className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="font-medium">No custom templates yet</p>
              <p className="text-sm mt-1">Create reusable document templates for your dealership</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {customTemplates.map(t => (
                <div key={t.id} className="bg-white rounded-xl border border-gray-100 p-5 space-y-3" data-testid={`custom-tpl-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.header_color || '#F59E0B' }} />
                        <h4 className="text-sm font-bold text-gray-900">{t.name}</h4>
                      </div>
                      {t.description && <p className="text-xs text-gray-500 mt-1">{t.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => editTemplate(t)} data-testid={`edit-tpl-${t.id}`}><Edit className="w-3.5 h-3.5 text-gray-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteTemplate(t.id)} data-testid={`del-tpl-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {t.sections?.map((s, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s.title} ({s.fields?.length || 0} fields)</span>
                    ))}
                  </div>
                  <Button onClick={() => { setActiveView('generate'); generateFromCustom(t.id); }} className="w-full rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs h-8" data-testid={`gen-custom-${t.id}`}>
                    <Printer className="w-3.5 h-3.5 mr-1.5" />Generate Document
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Placeholder helpers */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-700 mb-2">Auto-fill Placeholders</p>
            <div className="flex flex-wrap gap-1.5">
              {['{{vin}}','{{year}}','{{make}}','{{model}}','{{stock_number}}','{{color}}','{{mileage}}','{{buyer_name}}','{{buyer_phone}}','{{buyer_address}}','{{sale_price}}','{{total}}','{{dealer_name}}'].map(p => (
                <code key={p} className="text-[10px] px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-600 font-mono">{p}</code>
              ))}
            </div>
            <p className="text-[10px] text-blue-500 mt-2">Add these in the "Auto-fill" column to auto-populate from vehicle/customer data</p>
          </div>
        </div>
      )}

      {/* ==================== SIGNATURES VIEW ==================== */}
      {activeView === 'signatures' && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
                  <PenTool className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Digital Signatures</h3>
                  <p className="text-sm text-gray-500">Sign documents digitally — saved for reuse</p>
                </div>
              </div>

              {showSignaturePad ? (
                <SignaturePad
                  onSave={handleSaveSignature}
                  onCancel={() => { setShowSignaturePad(false); setActiveSignatureField(''); }}
                  label={activeSignatureField ? `Sign: ${activeSignatureField}` : 'Create Signature'}
                />
              ) : (
                <Button onClick={() => { setActiveSignatureField(''); setShowSignaturePad(true); }} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white h-11" data-testid="new-signature-btn">
                  <PenTool className="w-4 h-4 mr-2" />Create New Signature
                </Button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h4 className="text-sm font-bold text-gray-700 mb-4">Saved Signatures</h4>
            {savedSignatures.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <PenTool className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No saved signatures yet</p>
                <p className="text-xs mt-1">Create a signature to use in documents</p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedSignatures.map(sig => (
                  <div key={sig.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200" data-testid={`sig-${sig.id}`}>
                    <img src={sig.signature_data} alt={sig.label} className="h-12 w-24 object-contain bg-gray-50 rounded-lg border" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{sig.label}</p>
                      <p className="text-[10px] text-gray-400">{sig.created_at ? new Date(sig.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteSignature(sig.id)} data-testid={`del-sig-${sig.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="upload-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Upload Document</h3>
              <button onClick={() => setShowUpload(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <Select value={uploadForm.doc_type} onValueChange={v => setUploadForm(p => ({ ...p, doc_type: v }))}>
              <SelectTrigger className="rounded-xl" data-testid="upload-doc-type"><SelectValue placeholder="Document Type" /></SelectTrigger>
              <SelectContent>{docTypes.map(dt => <SelectItem key={dt.id} value={dt.id}>{dt.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <Input value={uploadForm.vin} onChange={e => setUploadForm(p => ({ ...p, vin: e.target.value }))} placeholder="VIN" className="rounded-xl" data-testid="upload-vin" />
              <Input value={uploadForm.stock_number} onChange={e => setUploadForm(p => ({ ...p, stock_number: e.target.value }))} placeholder="Stock #" className="rounded-xl" data-testid="upload-stock" />
            </div>
            <Input value={uploadForm.customer_name} onChange={e => setUploadForm(p => ({ ...p, customer_name: e.target.value }))} placeholder="Customer Name" className="rounded-xl" data-testid="upload-customer" />
            <Input type="date" value={uploadForm.expires_at} onChange={e => setUploadForm(p => ({ ...p, expires_at: e.target.value }))} className="rounded-xl" data-testid="upload-expires" />
            <textarea value={uploadForm.notes} onChange={e => setUploadForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes..." className="w-full border rounded-xl p-3 text-sm resize-none" rows={2} data-testid="upload-notes" />
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
              <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="upload-file-input" />
              <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-xl" data-testid="choose-file-btn">
                {uploading ? 'Uploading...' : 'Choose File'}
              </Button>
              <p className="text-xs text-gray-400 mt-2">PDF, images, or documents up to 25MB</p>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAudit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="audit-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Audit Log</h3>
              <button onClick={() => setShowAudit(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            {auditLog.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No activity yet</p>
            ) : auditLog.map((log, i) => (
              <div key={i} className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0" data-testid={`audit-${i}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                  log.action === 'upload' ? 'bg-green-100 text-green-600' :
                  log.action === 'download' ? 'bg-blue-100 text-blue-600' :
                  log.action === 'delete' ? 'bg-red-100 text-red-600' :
                  log.action === 'generate' ? 'bg-amber-100 text-amber-600' :
                  log.action === 'ai_scan' ? 'bg-violet-100 text-violet-600' : 'bg-gray-100 text-gray-600'
                }`}>{log.action?.[0]?.toUpperCase()}</div>
                <div>
                  <p className="text-sm text-gray-700">{log.details}</p>
                  <p className="text-xs text-gray-400">{log.user_name} {log.created_at ? `• ${new Date(log.created_at).toLocaleString()}` : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
